// src/components/SummaryData.js
import React, { useState, useEffect } from 'react';
import FilterTree from '../components/FilterTree';
import { generateTable, generateBarChart, generatePieChart, generateHistogram, generateScatterPlot } from '../utils/VisualizationUtils';
import { fetchData } from '../services/api';
import { FILTER_CONFIG, PARTY_COLORS, ACTIVE_FILTERS } from '../components/config';
import '../styles/SummaryData.css';

const SummaryPage = () => {
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [activeFilters, setActiveFilters] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [selectedVisualization, setSelectedVisualization] = useState('table');
  const [selectedColorScheme, setSelectedColorScheme] = useState('default');
  const [filterData, setFilterData] = useState({ 
    dataset: null, 
    labelKey: '', 
    dataKey: '', 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableVisualizations, setAvailableVisualizations] = useState(['table']);

  // Get available visualization types for the selected category
  const getAvailableVisualizations = (categoryKey) => {
    const category = categoryKey || (activeFilters && activeFilters.length > 0 ? activeFilters[0] : null);
    
    if (!category) return ['table'];
    
    const config = FILTER_CONFIG[category];
    
    if (!config || !config.validViz) return ['table'];
    
    // IMPORTANT FIX: Filter the visualization types based on their boolean values
    const validVizTypes = Object.entries(config.validViz)
      .filter(([_, isEnabled]) => isEnabled === true)
      .map(([vizType, _]) => vizType);
    
    return validVizTypes.length > 0 ? validVizTypes : ['table'];
  };

  // Handle filter changes from FilterTree component
  const handleFilterChange = async (filterPath) => {
    if (!filterPath || filterPath.length === 0) return;
    
    setActiveFilters(filterPath);
    setLoading(true);
    setError(null);
    
    try {
      const category = filterPath[0];
      const config = FILTER_CONFIG[category];
      
      if (!config) {
        setError('Invalid category selected');
        setLoading(false);
        return;
      }
      
      // Get available visualizations for this category
      const vizOptions = getAvailableVisualizations(category);
      setAvailableVisualizations(vizOptions);
      
      // If current visualization is not available in this category, switch to first available
      if (!vizOptions.includes(selectedVisualization)) {
        setSelectedVisualization(vizOptions[0]);
      }
      
      // Use the configuration for this category
      const endpoint = config.endpoint;
      const labelKey = config.labelKey;
      const dataKey = config.dataKey;
      
      // Build query parameters based on the filter path
      const params = {};
      
      // If we have a specific filter value selected (filterPath[1]), add it to the params
      if (filterPath.length > 1) {
        // Find which filters were selected and add them to params with correct API names
        const filtersConfig = config.validFilters;
        
        // Get available filter names for this category
        const filterNames = Object.keys(filtersConfig);
        
        // Match filterPath values to the corresponding filter names
        // This assumes filterPath values after the category are in the same order as available filters
        for (let i = 1; i < filterPath.length; i++) {
          if (filterPath[i] && i-1 < filterNames.length) {
            const filterName = filterNames[i-1];
            const apiName = filtersConfig[filterName].apiName;
            
            // Only include filters that should be active according to config
            if (ACTIVE_FILTERS[apiName] !== false) {
              params[apiName] = filterPath[i];
            }
          }
        }
      }
      
      // In debug mode, simulate API response
      if (debugMode) {
        let mockData;
        
        switch(category) {
          case 'all-candidates':
            mockData = [
              { CandidateName: 'Harris/Walz', PartyName: 'DEM', Year: '2024' },
              { CandidateName: 'Trump/Vance', PartyName: 'REP', Year: '2024' },
              { CandidateName: 'Begich, Nick', PartyName: 'REP', Year: '2024' },
              { CandidateName: 'Peltola, Mary S.', PartyName: 'DEM', Year: '2024' }
            ];
            break;
          case 'all-votes-by-party':
            mockData = [
              { PartyName: 'DEM', TotalVotes: 140026, Year: '2024', Percentage: 41.41 },
              { PartyName: 'REP', TotalVotes: 184458, Year: '2024', Percentage: 54.54 },
              { PartyName: 'LIB', TotalVotes: 8500, Year: '2024', Percentage: 2.51 },
              { PartyName: 'IND', TotalVotes: 5200, Year: '2024', Percentage: 1.54 }
            ];
            break;
          case 'all-votes-by-candidate':
            mockData = [
              { CandidateName: 'Harris/Walz', PartyName: 'DEM', TotalVotes: 140026, Year: '2024' },
              { CandidateName: 'Trump/Vance', PartyName: 'REP', TotalVotes: 184458, Year: '2024' },
              { CandidateName: 'Begich, Nick', PartyName: 'REP', TotalVotes: 159550, Year: '2024' },
              { CandidateName: 'Peltola, Mary S.', PartyName: 'DEM', TotalVotes: 152828, Year: '2024' }
            ];
            break;
          default:
            mockData = [];
        }
        
        setFilterData({
          dataset: mockData,
          labelKey,
          dataKey,
          partyKey: 'PartyName' // For party-based coloring
        });
      } else {
        // Make the actual API call
        const data = await fetchData(endpoint, params);
        
        setFilterData({
          dataset: data,
          labelKey,
          dataKey,
          partyKey: 'PartyName' // For party-based coloring
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error in handleFilterChange:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle visualization type changes
  const handleVisualizationChange = (vizType) => {
    setSelectedVisualization(vizType);
  };

  // Render visualization based on the selected type
  const renderVisualization = () => {
    if (loading) return <p className="loading-message">Loading data...</p>;
    if (error) return <p className="error-message">Error: {error}</p>;
    if (!filterData.dataset) return <p className="loading-message">Select filters to view data</p>;
    
    const options = {
      partyKey: filterData.partyKey,
      colorScheme: PARTY_COLORS
    };

    try {
      switch(selectedVisualization) {
        case 'table':
          return generateTable(filterData.dataset);
        case 'bar':
          return generateBarChart(
            filterData.dataset,
            filterData.labelKey,
            filterData.dataKey,
            options
          );
        case 'pie':
          return generatePieChart(
            filterData.dataset,
            filterData.labelKey,
            filterData.dataKey,
            options
          );
        case 'histogram':
          return generateHistogram(
            filterData.dataset,
            filterData.dataKey
          );
        case 'scatterplot':
          return generateScatterPlot(
            filterData.dataset,
            filterData.labelKey,
            filterData.dataKey,
            options
          );
        default:
          return generateTable(filterData.dataset);
      }
    } catch (e) {
      return <p className="error-message">Error rendering visualization: {e.message}</p>;
    }
  };

  // Initial setup on mount
  useEffect(() => {
    // Initialize available visualizations to just table
    setAvailableVisualizations(['table']);
  }, []);

  return (
    <div className="summary-page">
      {/* Fixed sidebar toggle button with span for vertical text */}
       <div className={`sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
        <div className="menu">
          <h3 className="menu-title">Dynamic Filters</h3>
          <label>
            Debug Mode:
            <input 
              type="checkbox" 
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
            />
          </label>
          
          <FilterTree 
            onFilterChange={handleFilterChange}
            debugMode={debugMode}
          />

          <div className="menu-item">
            <label>Visualization Type:</label>
            <select 
              value={selectedVisualization} 
              onChange={(e) => handleVisualizationChange(e.target.value)}
            >
              {availableVisualizations.map((type) => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={`content ${sidebarVisible ? 'with-sidebar' : 'full-width'}`}>
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarVisible(!sidebarVisible)}
        >
          <span>{sidebarVisible ? 'Hide Menu' : 'Show Menu'}</span>
        </button>
        
        <div className="data-display">
          <h3>Election Data Visualization</h3>
          <div className="viz">
            {renderVisualization()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;